// netlify/functions/lib/deployment-status.ts
//
// Plain-language answers to "what has QA detected, and what is it waiting for" — the read the project
// chat agent needs to explain PR and main-branch testing.
//
// The facts already exist (pr-views.ts computes awaiting_deployment / awaiting_deployment_since;
// version-monitor.ts records the main-branch fingerprint watch), but nothing turned them into something
// the agent could say. Without this it cannot see a PR revision at all until that revision's preview
// deployment lands: the round has no test session before then (versions.ts), and read_test_runs lists
// sessions — so "why hasn't QA started on my PR?" got "this project has no test runs".
//
// Pure and side-effect-free so it can be unit-tested standalone:
// `npx tsx netlify/functions/lib/deployment-status.test.ts`

// How long a PR may wait for its preview deployment URL before the wait is reported as a STALL rather
// than as progress — the poller's fast window (PREVIEW_FAST_PROBES minutes). Past that a preview
// normally would have appeared, so the honest read is that the build did not publish one. The frontend
// imports this same constant (src/lib/previewDeployment.ts), so chat, the GitHub tab and the Test Run
// page cannot disagree about which it is.
export const PREVIEW_STALL_AFTER_MS = 15 * 60 * 1000

// How long a parked task waits before the sweep gives up on it entirely. Mirrors PARKED_DEADLINE_MS in
// github-app/preview-deploys.ts; stated in the agent's answer so "still waiting" carries its own bound.
export const PREVIEW_GIVE_UP_AFTER_MS = 2 * 60 * 60 * 1000

// The fields of a PrSummary (github-app/pr-views.ts) this module reads. Narrowed rather than importing
// the whole shape so the formatter stays testable with plain literals.
export interface DeploymentPrFacts {
  pr_number: number
  branch_name: string
  head_sha: string
  pr_state: string
  run_status: string
  version_status: string
  deployed_url: string | null
  awaiting_deployment: boolean
  awaiting_deployment_since: string
  journeys_count: number
  bug_count: number
}

export interface MonitorFacts {
  status: string
  checks: number
  last_checked_at: string | null
  deadline: string
  last_error: string | null
  detected_sha: string | null
}

export function shortSha(sha: string | null | undefined): string {
  return (sha ?? '').slice(0, 12) || '(unknown sha)'
}

// "3h 20m" / "12m" / "40s" — a duration a person can read back to the user without doing arithmetic.
export function humanDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return 'an unknown time'
  const totalMinutes = Math.floor(ms / 60_000)
  if (totalMinutes < 1) return `${Math.max(0, Math.floor(ms / 1000))}s`
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (!hours) return `${minutes}m`
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`
}

function elapsedMs(since: string, nowMs: number): number | null {
  const started = new Date(since).getTime()
  if (Number.isNaN(started)) return null
  return nowMs - started
}

// What happened to this revision's deployment, and therefore whether QA ran — one sentence, stating the
// consequence rather than only the state, because "awaiting_deployment: true" tells the user nothing.
//
// Ordered by what SETTLES the question: a deployment we found means QA had something to test whatever
// else is true, while a revision the PR has moved past is finished no matter how its wait ended.
export function describePrDeployment(pr: DeploymentPrFacts, nowMs: number): string {
  if (pr.deployed_url) {
    return `preview deployment detected at ${pr.deployed_url} — QA ran against it (${pr.journeys_count} journey change(s), ${pr.bug_count} bug(s))`
  }
  if (pr.run_status === 'superseded') {
    return 'no preview deployment was detected before a newer commit was pushed, so this revision was never tested (the newer one has its own round)'
  }
  if (pr.pr_state === 'closed' || pr.pr_state === 'merged') {
    return `no preview deployment was detected before the pull request was ${pr.pr_state}, so this revision was never tested`
  }
  // Terminal version with no URL: the 2h sweep gave up (github-app/preview-deploys.ts), or the round was
  // cancelled. Either way nothing is still coming for it.
  if (pr.version_status === 'failed' || pr.version_status === 'cancelled') {
    return `no preview deployment was ever detected — QA gave up after ${humanDuration(PREVIEW_GIVE_UP_AFTER_MS)} and never tested this revision`
  }
  if (pr.awaiting_deployment) {
    const waited = elapsedMs(pr.awaiting_deployment_since, nowMs)
    const waitedText = waited === null ? '' : ` (waiting ${humanDuration(waited)} so far)`
    // Past the give-up deadline the wait is over in substance even if the sweep hasn't run yet, so don't
    // quote the deadline as something still ahead — that read as "waited 3h; gives up after 2h".
    if (waited !== null && waited >= PREVIEW_GIVE_UP_AFTER_MS) {
      return `NO preview deployment was ever detected for this commit${waitedText}, which is past the ${humanDuration(PREVIEW_GIVE_UP_AFTER_MS)} point where QA stops waiting — it was never tested, and publishing a preview now will not start it (a new commit would)`
    }
    if (waited !== null && waited >= PREVIEW_STALL_AFTER_MS) {
      return `NO preview deployment detected yet${waitedText} — long enough that the preview build most likely did not publish one. QA has NOT started and will not until a preview URL for this commit appears; it stops waiting ${humanDuration(PREVIEW_GIVE_UP_AFTER_MS)} after the commit`
    }
    return `waiting for this commit's preview deployment${waitedText} — QA has not started yet and starts automatically once the preview is published`
  }
  if (pr.version_status === 'in-progress') {
    return `deployment detected; QA is running now (${pr.journeys_count} journey change(s), ${pr.bug_count} bug(s) so far)`
  }
  return `QA ${pr.version_status} (${pr.journeys_count} journey change(s), ${pr.bug_count} bug(s))`
}

// One line per PR revision. `testRunId` is the round's test run when it has one — a revision only gets a
// session once its deployment is found, so its absence is itself part of the answer.
export function prDeploymentLine(
  pr: DeploymentPrFacts,
  nowMs: number,
  testRunId: string | null,
): string {
  const run = testRunId
    ? `test run ${testRunId}`
    : 'no test run yet (a round is created when the deployment is detected)'
  return `• PR #${pr.pr_number} (${pr.branch_name}, commit ${shortSha(pr.head_sha)}) [PR ${pr.pr_state}] — ${describePrDeployment(pr, nowMs)}; ${run}`
}

// The main-branch half. `mode` is the project's github.mainBranchTesting, whose three reachable values
// detect a deployment in three different ways — so the honest answer to "I pushed to main and nothing
// happened" depends entirely on which one is set.
export function mainBranchDeploymentLines(
  mode: string | null | undefined,
  deploymentUrl: string | null | undefined,
  active: MonitorFacts | null,
  recent: MonitorFacts[],
  nowMs: number,
): string[] {
  const lines: string[] = []
  if (mode === 'detect') {
    const url = deploymentUrl?.trim()
    lines.push(
      url
        ? `Main-branch testing: watching ${url} for a new deployment. A push does NOT start QA by itself — QA starts when the app served at that URL changes (its fingerprint), which is how a new deployment going live is detected.`
        : 'Main-branch testing is set to detect deployments but NO deployment URL is configured, so nothing can be watched and a push to main starts nothing. Setting the main-branch deployment URL is what unblocks it.',
    )
    if (active) {
      const last = active.last_checked_at ? `last checked ${active.last_checked_at}` : 'not checked yet'
      const deadline = elapsedMs(active.deadline, nowMs)
      const deadlineText = deadline !== null && deadline < 0 ? `, gives up in ${humanDuration(-deadline)}` : ''
      lines.push(`  • Watching now: ${active.checks} check(s), ${last}${deadlineText}. No change seen yet, so QA has not started for the latest push.`)
      if (active.last_error) lines.push(`  • Last check error (the app may be unreachable): ${active.last_error}`)
    } else if (recent.length) {
      const [latest] = recent
      lines.push(
        latest!.status === 'detected'
          ? `  • Not watching right now. The last watch DETECTED a deployment (${shortSha(latest!.detected_sha)}) and started QA on it.`
          : `  • Not watching right now. The last watch ended '${latest!.status}' after ${latest!.checks} check(s) without seeing the app change${latest!.last_error ? ` (last error: ${latest!.last_error})` : ''} — so that push was never tested.`,
      )
    } else {
      lines.push('  • No deployment watch has run yet for this project.')
    }
    return lines
  }
  if (mode === 'events') {
    lines.push("Main-branch testing: waiting for GitHub to report a main-branch deployment (a deployment_status event, e.g. from Netlify or Vercel). A push alone does NOT start QA — if the host never reports a deployment to GitHub, nothing starts.")
    return lines
  }
  if (mode === 'none' || !mode) {
    lines.push('Main-branch testing is OFF, so pushes to the main branch never start QA.')
    return lines
  }
  lines.push(`Main-branch testing mode is '${mode}', which does not start QA from a push.`)
  return lines
}

// The standing facts about how a PR's deployment is found. The agent needs these to explain a wait — the
// channels are all we can see, so a repo that publishes none of them can never have its PRs tested, and
// that is the answer rather than a bug to investigate.
export const PR_DEPLOYMENT_DETECTION_NOTES = [
  'How a pull request gets tested: pushing a commit is detected immediately (that is what creates the',
  "entry above), but QA is HELD until that exact commit's own preview deployment URL is known. It is",
  'never run against the main-branch URL, because that would report results for code the PR does not',
  'contain. The preview URL is picked up from any of: a GitHub deployment status, a commit status, a',
  'Netlify/Vercel bot comment on the pull request, or ~2h of polling GitHub for one. If a repository',
  'publishes none of those, QA never starts for its pull requests — the fix is to publish (or re-run) the',
  'preview build, or to turn pull-request testing off so it stops waiting.',
].join(' ')
